import { Money, Status } from "../../../../components/ui";
import { shortDate } from "../../../../components/calendar/calendarDays";

// Report 54A (D1): how a pay was worked out, as the server stored it. One presentation for the employee's payslip,
// the stored HR payslip, the printed payslip, the HR payroll review and the Finance payroll review. Nothing here
// calculates pay: every amount, count, rate and sentence comes from `payslip.calculation` (or a review member's
// `calculation`), which the server builds from the payslip's stored lines. Unpaid days are steps inside basic pay;
// they are never listed again as deductions.

const day = (date) => shortDate(String(date).slice(0, 10), { year: false });

/** "6 Oct, 8 Oct" — the dates behind a step, when the payroll stored them. */
export function datesText(dates = []) {
  return dates.length ? dates.map(day).join(", ") : "";
}

const table = "mt-2 w-full border-collapse border-t border-default text-body-sm";

function Amount({ line }) {
  if (line.effect === "info") return <span className="text-secondary">No change</span>;
  return <Money value={line.effect === "subtract" ? -Number(line.amountCents || 0) : line.amountCents} />;
}

/**
 * The steps from the pay record to the basic pay: what the period's pay starts from, what unpaid days took out of it
 * (or, for daily and hourly pay, the time that earned it), and the basic pay that results.
 * `calculation.basic.stored` is false for a payslip calculated before the steps were kept.
 */
export function BasicPayWorking({ calculation, caption = "How the basic pay was worked out" }) {
  const basic = calculation?.basic;
  if (!basic) return null;
  if (!basic.stored) {
    return (
      <p className="mt-2 text-body-sm text-secondary">
        {Number(basic.unpaidDaysCents || 0) > 0 ? (
          <>Basic pay is shown after <Money value={basic.unpaidDaysCents} className="text-foreground" /> was taken out for unpaid days. </>
        ) : null}
        The day-by-day working was not stored when this payslip was calculated, so only the stored totals are shown.
      </p>
    );
  }
  return (
    <>
    {/* Report 55: the calendar the pay was counted on, stored with the payslip. */}
    {basic.calendar?.formula ? <p className="mt-2 text-meta text-secondary">Counted on {basic.calendar.formula}.</p> : null}
    <table data-ui="payslip-table" className={table}>
      <caption className="sr-only">{caption}</caption>
      <thead className="sr-only">
        <tr>
          <th scope="col">Step</th>
          <th scope="col">Amount</th>
        </tr>
      </thead>
      <tbody>
        {basic.lines.map((line, index) => (
          <tr key={`${line.code}-${index}`} className="border-b border-subtle">
            <th scope="row" className="whitespace-normal py-2 pr-4 text-left align-top font-normal text-foreground">
              {line.effect === "subtract" ? "Less: " : ""}{line.label}
              {line.formula ? <span className="mt-0.5 block text-meta text-secondary">{line.formula}</span> : null}
              {line.dates?.length ? <span className="mt-0.5 block text-meta text-secondary">{line.dates.length === 1 ? "Date" : "Dates"}: {datesText(line.dates)}</span> : null}
            </th>
            <td className="whitespace-nowrap py-2 text-right align-top text-foreground"><Amount line={line} /></td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-b border-default">
          <th scope="row" className="py-2.5 pr-4 text-left font-semibold text-foreground">Basic pay</th>
          <td className="whitespace-nowrap py-2.5 text-right font-semibold text-foreground"><Money value={basic.payableCents} /></td>
        </tr>
      </tfoot>
    </table>
    </>
  );
}

/**
 * Report 55: a rest-day or night pay that is not owed (an approved exception) or not decided yet, said in words so the
 * reader knows it was considered. No amount.
 */
export function EarningNotes({ calculation }) {
  const notes = calculation?.earnings?.notes || [];
  if (!notes.length) return null;
  return (
    <ul className="mt-2 space-y-1 text-meta text-secondary">
      {notes.map((line, index) => (
        <li key={`${line.code}-${index}`}>
          {line.label}{line.dates?.length ? ` (${datesText(line.dates)})` : ""}: {line.formula}
        </li>
      ))}
    </ul>
  );
}

function Rows({ lines, empty }) {
  if (!lines.length) {
    return (
      <tr className="border-b border-subtle">
        <td colSpan={2} className="py-2 text-secondary">{empty}</td>
      </tr>
    );
  }
  return lines.map((line, index) => (
    <tr key={`${line.code}-${index}`} className="border-b border-subtle">
      <th scope="row" className="whitespace-normal py-2 pr-4 text-left align-top font-normal text-foreground">
        {line.label}
        {line.formula || line.note ? <span className="mt-0.5 block text-meta text-secondary">{line.formula || line.note}</span> : null}
        {line.dates?.length ? <span className="mt-0.5 block text-meta text-secondary">{datesText(line.dates)}</span> : null}
      </th>
      <td className="whitespace-nowrap py-2 text-right align-top text-foreground">
        {line.pending ? <span className="text-secondary">Not decided yet</span> : <Money value={line.amountCents} />}
      </td>
    </tr>
  ));
}

function Foot({ label, cents, closing = false }) {
  return (
    <tr className={closing ? "border-b-[3px] border-double border-strong" : "border-b border-default"}>
      <th scope="row" className="py-2.5 pr-4 text-left font-semibold text-foreground">{label}</th>
      <td className="whitespace-nowrap py-2.5 text-right font-semibold text-foreground"><Money value={cents} /></td>
    </tr>
  );
}

/** Gross pay − total deductions = net pay, in one line of stored figures. */
export function NetPayEquation({ calculation, className = "" }) {
  const net = calculation?.net;
  if (!net) return null;
  return (
    <p className={`text-body-sm text-secondary ${className}`}>
      <span className="whitespace-nowrap">Gross pay <Money value={net.grossCents} className="text-foreground" /></span>{" − "}
      <span className="whitespace-nowrap">total deductions <Money value={net.deductionsCents} className="text-foreground" /></span>{" = "}
      <span className="whitespace-nowrap">net pay <Money value={net.netCents} className="font-semibold text-foreground" /></span>
    </p>
  );
}

/** Shown only when the stored parts do not add up to the stored totals: a fact to act on, never hidden. */
export function CalculationCheck({ calculation }) {
  if (!calculation || calculation.checks?.ok) return null;
  return (
    <p role="alert" className="mt-2 text-body-sm">
      <Status tone="danger" icon>The lines of this pay do not add up to its stored totals. Recalculate the payroll before relying on it.</Status>
    </p>
  );
}

/**
 * The whole working of one pay for a reviewer (HR before sending to Finance, Finance before approving): basic pay
 * steps, every earning, every deduction, and the closing equation. Read only.
 */
export default function PayCalculation({ calculation, name }) {
  if (!calculation) return null;
  const who = name ? ` for ${name}` : "";
  return (
    <div className="space-y-5">
      <section aria-label={`Basic pay${who}`}>
        <h4 className="text-label text-foreground">
          Basic pay{calculation.basic.payBasisLabel ? <span className="font-normal text-secondary"> · {calculation.basic.payBasisLabel}</span> : null}
        </h4>
        <BasicPayWorking calculation={calculation} caption={`How the basic pay was worked out${who}`} />
      </section>
      <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
        <section aria-label={`Earnings${who}`}>
          <h4 className="text-label text-foreground">Earnings</h4>
          <table data-ui="payslip-table" className={table}>
            <caption className="sr-only">Earnings{who}</caption>
            <tbody><Rows lines={calculation.earnings.lines} empty="No earnings." /></tbody>
            <tfoot><Foot label="Gross pay" cents={calculation.earnings.grossCents} /></tfoot>
          </table>
          <EarningNotes calculation={calculation} />
        </section>
        <section aria-label={`Deductions${who}`}>
          <h4 className="text-label text-foreground">Deducted from pay</h4>
          <table data-ui="payslip-table" className={table}>
            <caption className="sr-only">Deducted from pay{who}</caption>
            <tbody><Rows lines={calculation.deductions.lines} empty="Nothing is deducted." /></tbody>
            <tfoot>
              <Foot label="Total deductions" cents={calculation.deductions.totalCents} />
              <Foot closing label="Net pay" cents={calculation.net.netCents} />
            </tfoot>
          </table>
        </section>
      </div>
      <NetPayEquation calculation={calculation} />
      {calculation.employerPaid.lines.length ? (
        <p className="text-meta text-tertiary">
          The business also pays <Money value={calculation.employerPaid.totalCents} /> in employer contributions on top of this pay. It is not deducted from the employee.
        </p>
      ) : null}
      <CalculationCheck calculation={calculation} />
    </div>
  );
}
